// src/components/GlobalHeader.jsx
import { useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { CAS_OPTIONS } from "../api/api";

export const GlobalFilterContext = createContext({ cas: "9" });
export const useGlobalFilter = () => useContext(GlobalFilterContext);

// ─────────────────────────────────────────────────────────────────────────────
// HeaderSlot
//
// Lets a page (e.g. SalesOverview) render controls INTO the navbar, right
// next to "Chairman's Desk", instead of GlobalHeader needing to know
// anything about that page's state. GlobalHeader exposes the DOM node it
// wants filled via context; the page portals into it with
// ReactDOM.createPortal(<Whatever/>, headerSlot).
// ─────────────────────────────────────────────────────────────────────────────
export const HeaderSlotContext = createContext(null);
export const useHeaderSlot = () => useContext(HeaderSlotContext);

const ALL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  /* ── shared ── */
  @keyframes pt-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes pt-dot-blink {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
  }
  @keyframes pt-fadein {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pb-icon-drop {
    0%   { opacity: 0; transform: translateY(-10px) scale(0.7); }
    65%  { transform: translateY(3px) scale(1.1); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes pb-badge-pop {
    0%   { opacity: 0; transform: scale(0.7) translateY(4px); }
    70%  { transform: scale(1.08); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes pb-bar-grow {
    from { width: 0; opacity: 0; }
    to   { width: 100%; opacity: 1; }
  }
  @keyframes pb-tag-pop {
    0%   { opacity: 0; transform: scale(0.8); }
    70%  { transform: scale(1.06); }
    100% { opacity: 1; transform: scale(1); }
  }

  /* ── PageBanner ── */
  .pb-wrap {
    display: flex;
    flex-direction: column;
    gap: 0px;
    margin-bottom: 28px;
    animation: pt-fadein 0.4s ease both;
  }
  .pb-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
    margin-bottom: 10px;
  }
  .pb-icon {
    font-size: 35px;
    line-height: 0.5;
    animation: pb-icon-drop 0.5s cubic-bezier(0.22,1,0.36,1) both 0.05s;
  }
  .pb-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0;
    background: linear-gradient(90deg, #1e293b 0%, #4f46e5 50%, #1e293b 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: pt-shimmer 5s linear 1s infinite;
  }
  .pb-live {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 9px;
    font-weight: 700;
    color: #059669;
    background: #d1fae5;
    border: 1px solid #a7f3d0;
    border-radius: 999px;
    padding: 2px 8px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    animation: pb-badge-pop 0.45s cubic-bezier(0.22,1,0.36,1) both 0.3s;
  }
  .pb-live-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #10b981;
    animation: pt-dot-blink 1.4s ease-in-out infinite;
  }
  .pb-badge {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 600;
    color: #4338ca;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    border-radius: 6px;
    padding: 2px 10px;
    animation: pb-badge-pop 0.45s cubic-bezier(0.22,1,0.36,1) both 0.38s;
  }
  .pb-bar-wrap {
    position: relative;
    height: 2px;
    border-radius: 999px;
    background: #e8edf5;
    overflow: hidden;
    width: 20%;
  }
  .pb-bar-fill {
    position: absolute;
    left: 0; top: 0;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #6366f1 0%, #38bdf8 30%, #10b981 55%, #38bdf8 80%, #6366f1 100%);
    background-size: 300% 100%;
    animation:
      pb-bar-grow 0.75s cubic-bezier(0.22,1,0.36,1) both 0.1s,
      pt-shimmer 4s linear 1s infinite;
  }
  .pb-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    border-radius: 999px;
    padding: 2px 9px;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }
  .pb-tag-green  { color: #059669; background: #d1fae5; border: 1px solid #a7f3d0; }
  .pb-tag-blue   { color: #2563eb; background: #dbeafe; border: 1px solid #bfdbfe; }
  .pb-tag-purple { color: #7c3aed; background: #ede9fe; border: 1px solid #ddd6fe; }
  .pb-tag-amber  { color: #d97706; background: #fef3c7; border: 1px solid #fde68a; }
  .pb-tag-slate  { color: #475569; background: #f1f5f9; border: 1px solid #e2e8f0; }

  /* ── Inline subtitle pill ── */
  @keyframes pb-subtitle-slide {
    from { opacity: 0; transform: translateX(14px) scale(0.9); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes pb-subtitle-glow {
    0%, 100% { box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 0 0 0px rgba(99,102,241,0.15); }
    50%       { box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 0 0 3px rgba(99,102,241,0.08); }
  }
  .pb-subtitle-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    color: #475569;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1.5px solid #e2e8f0;
    border-radius: 999px;
    padding: 7px 18px 7px 12px;
    white-space: nowrap;
    cursor: default;
    animation:
      pb-subtitle-slide 0.52s cubic-bezier(0.22,1,0.36,1) both 0.5s,
      pb-subtitle-glow 3s ease-in-out 1.2s infinite;
    transition: background 0.22s ease, border-color 0.22s ease, color 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
  }
  .pb-subtitle-pill:hover {
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    border-color: #c7d2fe;
    color: #3730a3;
    box-shadow: 0 3px 10px rgba(99,102,241,0.15);
    transform: translateY(-1px);
  }
  .pb-subtitle-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #10b981);
    flex-shrink: 0;
    animation: pt-dot-blink 2s ease-in-out infinite;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
  }

  /* ── Active Search card animations ── */
  @keyframes as-slidein {
    from { opacity: 0; transform: translateX(24px) scale(0.96); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes as-pulse-ring {
    0%   { transform: scale(1);    opacity: 0.6; }
    70%  { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }
  @keyframes as-count-pop {
    0%   { transform: scale(0.5); opacity: 0; }
    65%  { transform: scale(1.25); }
    100% { transform: scale(1);   opacity: 1; }
  }
  @keyframes as-label-fadein {
    from { opacity: 0; letter-spacing: 0.12em; }
    to   { opacity: 1; letter-spacing: 0.5px; }
  }
  @keyframes as-icon-spin {
    0%   { transform: rotate(-15deg) scale(0.8); opacity: 0; }
    60%  { transform: rotate(8deg) scale(1.1); opacity: 1; }
    100% { transform: rotate(0deg) scale(1); opacity: 1; }
  }
  @keyframes as-value-pop {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes as-border-glow {
    0%, 100% { box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 0 0 0px rgba(20,184,166,0.3); }
    50%       { box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 0 0 3px rgba(20,184,166,0.15); }
  }
  @keyframes as-divider-grow {
    from { height: 0; opacity: 0; }
    to   { height: 32px; opacity: 1; }
  }

  .as-card {
    display: inline-flex !important;
    align-items: center !important;
    border-radius: 10px !important;
    background: #ffffff !important;
    border: 1px solid #d1faf5 !important;
    overflow: hidden !important;
    flex-shrink: 0 !important;
    animation:
      as-slidein 0.42s cubic-bezier(0.22,1,0.36,1) both,
      as-border-glow 2.8s ease-in-out 0.5s infinite !important;
  }
  .as-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    text-transform: uppercase !important;
    white-space: nowrap !important;
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 40%, #5eead4 70%, #14b8a6 100%) !important;
    background-size: 200% auto !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
    background-clip: text !important;
    animation:
      as-label-fadein 0.5s ease both 0.15s,
      pt-shimmer 3s linear 1s infinite !important;
  }
  .as-count-wrap {
    position: relative !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
  }
  .as-count-ring {
    position: absolute !important;
    inset: 0 !important;
    border-radius: 50% !important;
    background: #14b8a6 !important;
    animation: as-pulse-ring 2s ease-out 0.4s infinite !important;
  }
  .as-count-bubble {
    position: relative !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 20px !important;
    height: 20px !important;
    border-radius: 50% !important;
    background: #14b8a6 !important;
    color: #fff !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    animation: as-count-pop 0.5s cubic-bezier(0.22,1,0.36,1) both 0.2s !important;
  }
  .as-divider {
    width: 1px !important;
    height: 32px !important;
    background: #d1faf5 !important;
    flex-shrink: 0 !important;
    animation: as-divider-grow 0.35s ease both 0.3s !important;
  }
  .as-icon {
    font-size: 20px !important;
    line-height: 1 !important;
    animation: as-icon-spin 0.5s cubic-bezier(0.22,1,0.36,1) both 0.25s !important;
  }
  .as-type-label {
    font-size: 9px !important;
    font-weight: 600 !important;
    color: #9ca3af !important;
    letter-spacing: 0.5px !important;
    line-height: 1 !important;
    text-transform: uppercase !important;
  }
  .as-type-value {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #1f2937 !important;
    line-height: 1.2 !important;
    animation: as-value-pop 0.4s ease both 0.35s !important;
  }
  .as-clear {
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    padding: 8px 16px !important;
    background: transparent !important;
    border: none !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #6b7280 !important;
    cursor: pointer !important;
    transition: color 0.15s ease, background 0.15s ease !important;
    white-space: nowrap !important;
  }
  .as-clear:hover { color: #ef4444 !important; background: #fff5f5 !important; }
  .as-clear-icon {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 16px !important;
    height: 16px !important;
    border-radius: 50% !important;
    border: 1.5px solid currentColor !important;
    font-size: 10px !important;
    line-height: 1 !important;
    font-weight: 700 !important;
    transition: transform 0.2s ease !important;
  }
  .as-clear:hover .as-clear-icon { transform: rotate(90deg) !important; }

  /* ── Sales Overview responsive overrides (mobile + tablet only) ── */
  @media (max-width: 1024px) {
    .gh-so-row {
      flex-wrap: wrap !important;
      gap: 12px !important;
    }
    .gh-so-right {
      width: 100% !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
    }
    .gh-so-slot {
      width: 100% !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
    }
    .gh-so-slot > * {
      flex-shrink: 0 !important;
    }
  }
  @media (max-width: 767px) {
    .gh-so-title {
      display: none !important;
    }
    .gh-so-row {
      margin-bottom: 12px !important;
    }
    .gh-so-slot {
      gap: 8px !important;
    }
    .gh-so-slot > * {
      width: 100% !important;
    }
  }
    @media (max-width: 768px) {
    .header-title {
    font-size: 1.75rem;
    line-height: 1.2;
  }

  /* ── General GlobalHeader responsive overrides — all other pages ──
     Desktop (>1024px) is completely untouched below: same row, same
     negative-margin alignment against the tall letter-animated title,
     same fixed 120px CAS pills. These rules only kick in on tablet/mobile,
     where that fixed-width, no-wrap desktop row would otherwise overflow
     or clip the CAS pills / Active Search card off-screen. ── */
  @media (max-width: 1024px) {
    .gh-header-row {
      flex-wrap: wrap !important;
      gap: 14px !important;
      margin-bottom: 10px !important;
    }
    .gh-header-left {
      flex-wrap: wrap !important;
      gap: 12px !important;
      width: 100% !important;
    }
    .gh-cas-pills {
      margin-top: 0 !important;
      flex-wrap: wrap !important;
      width: 100% !important;
    }
    .gh-header-right {
      margin-top: 0 !important;
      width: 100% !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
    }
    .as-card {
      margin-top: 0 !important;
    }
  }
  @media (max-width: 767px) {
    .gh-cas-pills {
      gap: 8px !important;
    }
    .gh-cas-pills button {
      width: auto !important;
      flex: 1 1 auto !important;
      min-width: 84px !important;
      padding: 9px 6px !important;
      font-size: 12.5px !important;
    }
    .as-card {
      display: flex !important;
      flex-wrap: wrap !important;
      width: 100% !important;
    }
    .as-section {
      flex: 1 1 auto !important;
    }
    .as-divider {
      display: none !important;
    }
    .as-clear {
      width: 100% !important;
      justify-content: center !important;
      border-top: 1px solid #d1faf5 !important;
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// PageBanner — reusable across all pages
// ─────────────────────────────────────────────────────────────────────────────
export function PageBanner({
  icon = "📄",
  title = "",
  subtitle = "",
  live = true,
  badge = "",
  tags = [],
}) {
  return (
    <div className="pb-wrap">
      {/* ── Single row: icon · title · LIVE · badge · tags · subtitle pill ── */}
      <div className="pb-title-row">
        <span className="pb-icon">{icon}</span>
        <h2 className="pb-title">{title}</h2>

        {live && (
          <span className="pb-live">
            <span className="pb-live-dot" />
            LIVE
          </span>
        )}

        {badge && <span className="pb-badge">{badge}</span>}

        {tags.map((t, i) => (
          <span
            key={i}
            className={`pb-tag pb-tag-${t.color || "slate"}`}
            style={{
              animationDelay: `${0.25 + i * 0.07}s`,
              animation: "pb-tag-pop 0.4s cubic-bezier(0.22,1,0.36,1) both",
            }}
          >
            {t.label}
          </span>
        ))}

        {/* ── Subtitle now lives inline right after the last tag ── */}
        {subtitle && (
          <span className="pb-subtitle-pill">
            <span className="pb-subtitle-dot" />
            {subtitle}
          </span>
        )}
      </div>

      {/* ── Animated gradient underbar ── */}
      <div className="pb-bar-wrap">
        <div className="pb-bar-fill" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GlobalHeader
// ─────────────────────────────────────────────────────────────────────────────
export default function GlobalHeader({ children }) {
  const [selectedCas, setSelectedCas] = useState("9");
  const [cardKey, setCardKey] = useState(0);

  // DOM node that pages can portal controls into, right next to
  // "Chairman's Desk" — see HeaderSlotContext above. Using state (not a
  // plain ref) so that, once the placeholder <div> mounts, the context
  // value updates and any page consuming useHeaderSlot() re-renders with
  // a real, portal-able node.
  const [slotEl, setSlotEl] = useState(null);

  const location = useLocation();
  // The CAS (STB/ONT/APP) pills + "Active Search" card are only meaningful
  // for pages that actually filter by CAS type. Sales Overview doesn't use
  // this filter at all, so it's hidden there — add more paths to this list
  // if other pages need the same treatment later.
  const hideCasFilter = location.pathname === "/sales-overview";

  const activeCasOption = CAS_OPTIONS.find((o) => o.value === selectedCas);

  const activeFilterBadges = [
    selectedCas && {
      key: "cas",
      icon: activeCasOption?.icon || "📡",
      label: "Type",
      value: activeCasOption?.label || selectedCas,
    },
  ].filter(Boolean);

  const isFiltered = activeFilterBadges.length > 0;

  const handleSelect = (value) => {
    setSelectedCas(value);
    setCardKey((k) => k + 1);
  };

  const casPillStyle = (opt) => {
    const isSelected = selectedCas === opt.value;
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "7px",
      width: "120px",
      padding: "11px 0",
      borderRadius: "999px",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "14px",
      transition: "all 0.18s ease",
      border: `2px solid ${isSelected ? opt.color : "#e2e8f0"}`,
      background: isSelected ? opt.color : "#fff",
      color: isSelected ? "#fff" : "#64748b",
      boxShadow: isSelected
        ? `0 4px 14px ${opt.color}44`
        : "0 1px 3px rgba(0,0,0,0.06)",
      transform: isSelected ? "translateY(-1px)" : "none",
      flexShrink: 0,
      whiteSpace: "nowrap",
    };
  };

  return (
    <GlobalFilterContext.Provider value={{ cas: selectedCas }}>
      {/* ── Inject styles — always present, never races ── */}
      <style dangerouslySetInnerHTML={{ __html: ALL_CSS }} />

      {/* ── Single horizontal row ── */}
      {/* gh-header-row/left/right + gh-cas-pills apply the general
          tablet/mobile wrapping rules on every page. gh-so-row / gh-so-title
          / gh-so-right layer on Sales-Overview-specific rules on top (see
          media queries in ALL_CSS above) — desktop (>1024px) is identical
          to before on every page. */}
      <div
        className={`gh-header-row ${hideCasFilter ? "gh-so-row" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "20px",
          marginBottom: "-17px",
          flexWrap: "nowrap",
        }}
      >
        {/* LEFT: title block + pills */}
        <div
          className="gh-header-left"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "32px",
            minWidth: 0,
          }}
        >
          {/* ── Original title — hidden on mobile for Sales Overview ── */}
          <div className={`header ${hideCasFilter ? "gh-so-title" : ""}`}>
            <div className="header-inner">
              <h1 className="header-title">
                {"Chairman's Desk".split("").map((char, i) => (
                  <span
                    key={i}
                    className="header-letter"
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    {char === " " ? "\u00A0" : char}
                  </span>
                ))}
              </h1>
              <div className="header-underline">
                <div className="header-underline-fill" />
              </div>
              <p className="header-subtitle">
                Real-time Network Inventory &amp; Operations Overview
              </p>
            </div>
          </div>

          {/* Pills — hidden on pages that don't use the CAS filter */}
          {!hideCasFilter && (
            <div
              className="gh-cas-pills"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexShrink: 0,
                marginTop: "-24px",
              }}
            >
              {CAS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  style={casPillStyle(opt)}
                  onClick={() => handleSelect(opt.value)}
                >
                  <span style={{ fontSize: "15px" }}>{opt.icon}</span>
                  {opt.label}
                  {selectedCas === opt.value && (
                    <span
                      style={{
                        fontSize: "11px",
                        background: "rgba(255,255,255,0.3)",
                        borderRadius: "999px",
                        padding: "1px 5px",
                      }}
                    >
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: top-right slot for page-supplied controls (e.g. Sales   */}
        {/* Overview's month/Search/Set Target bar), plus the Active       */}
        {/* Search card on pages that use the CAS filter.                  */}
        <div
          className={`gh-header-right ${hideCasFilter ? "gh-so-right" : ""}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexShrink: 0,
            marginTop: "-24px",
          }}
        >
          {/* Pages portal into this node via useHeaderSlot() +            */}
          {/* ReactDOM.createPortal(...). Empty on pages that don't use it,*/}
          {/* so it takes no visual space there. On Sales Overview, the    */}
          {/* gh-so-slot class lets the portaled month/search/set-target   */}
          {/* controls wrap onto their own lines on tablet/mobile instead  */}
          {/* of overflowing off-screen. */}
          <div
            ref={setSlotEl}
            className={hideCasFilter ? "gh-so-slot" : ""}
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          />

          {isFiltered && !hideCasFilter && (
            <div
              key={cardKey}
              className="as-card"
              style={{ marginTop: "-24px" }}
            >
              <div
                className="as-section"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                }}
              >
                <span className="as-label">Active Search</span>
                <div className="as-count-wrap">
                  <div className="as-count-ring" />
                  <span className="as-count-bubble">
                    {activeFilterBadges.length}
                  </span>
                </div>
              </div>

              <div className="as-divider" />

              <div
                className="as-section"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                }}
              >
                <span className="as-icon">{activeCasOption?.icon || "📡"}</span>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1px",
                  }}
                >
                  <span className="as-type-label">Type</span>
                  <span className="as-type-value">
                    {activeCasOption?.label || selectedCas}
                  </span>
                </div>
              </div>

              <div className="as-divider" />

              <button className="as-clear" onClick={() => setSelectedCas("")}>
                <span className="as-clear-icon">✕</span>
                Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      <HeaderSlotContext.Provider value={slotEl}>
        {children}
      </HeaderSlotContext.Provider>
    </GlobalFilterContext.Provider>
  );
}
